import './AgentPlanBuy.scss';
import { useEffect, useState } from 'react';
import { MdOutlineReceiptLong } from 'react-icons/md';
import { AgentSessionManager } from '@Components/AgentChat';
import { Button } from '@Components/Button';
import { Drawer } from '@Components/Drawer';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetError } from '../../redux/modals';
import { Agent, useAgentEndpoint } from '../../utils/agent-endpoints';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { countAgentOrdersNeedingApproval, findNewDrafts } from './agent-orders-utils';
import { OrdersPanel } from './OrdersPanel';
import { useAgentOrders } from './use-agent-orders';

// The media-planning agent chat from the Elevate demo's Media Plan step, without the
// Elevate-specific handoff context, so any client can run intake -> plan. The orders panel beside
// it lists the orders the agent drafted, for a person to approve.
export const AgentPlanBuy = () => {
  const { cid: company } = useCompanyInfo();
  const agentEndpoint = useAgentEndpoint(Agent.MEDIA_PLANNING);
  const setError = useSetError();
  const [ordersOpen, setOrdersOpen] = useState(true);
  const [newOrderIds, setNewOrderIds] = useState<number[]>([]);

  const agentOrders = useAgentOrders(company);
  const { data: orderInfo, error, refetch } = agentOrders;

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load orders",
        message: `Failed to load the agent's orders: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const draftCount = orderInfo ? countAgentOrdersNeedingApproval(orderInfo) : 0;

  // The agent may have drafted or sent orders in this reply. Open the panel on any new draft.
  const onAgentResponse = async () => {
    // Orders not loaded before this reply: nothing to compare against, so highlight nothing.
    const previousIds = orderInfo && new Set(orderInfo.map(order => order.id));
    const { data } = await refetch();
    if (!previousIds) return;
    const newDrafts = findNewDrafts(previousIds, data ?? []);
    setNewOrderIds(newDrafts);
    if (newDrafts.length > 0) {
      setOrdersOpen(true);
    }
  };

  return (
    <Page
      title={PAGE_NAMES.AGENT_PLAN_BUY}
      pageType={PAGE_NAMES.AGENT_PLAN_BUY}
      actions={
        !ordersOpen && (
          <Button leadingElement={<MdOutlineReceiptLong />} onClick={() => setOrdersOpen(true)}>
            {draftCount ? `View orders (${draftCount} to approve)` : 'View orders'}
          </Button>
        )
      }
    >
      <div className='agent-plan-buy'>
        <div className='agent-plan-buy-main'>
          <AgentSessionManager
            agentEndpoint={agentEndpoint}
            agent={Agent.MEDIA_PLANNING}
            company={company}
            emptyState={
              <p className='agent-plan-buy-blurb'>
                Pick a prompt below (or type your own) to build a streaming line-item plan.
              </p>
            }
            suggestedPrompts={[
              'Build a streaming line-item plan for this client.',
              'Walk me through the plan you would build and why, before drafting line items.'
            ]}
            onAgentResponse={onAgentResponse}
            className='agent-plan-buy-chat'
          />
        </div>
        <Drawer
          title={draftCount ? `Orders (${draftCount} to approve)` : 'Orders'}
          // The page header's "View orders" button opens it; a corner toggle would cover the chat's send button.
          hideToggle
          open={ordersOpen}
          onOpenChange={setOrdersOpen}
        >
          <OrdersPanel agentOrders={agentOrders} newOrderIds={newOrderIds} />
        </Drawer>
      </div>
    </Page>
  );
};
